import { FC, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Formik } from "formik"
import { object, string } from "yup"
import { Settings } from "lucide-react"
import {
  RateCardsConfigDetails,
  RateCardsConfigDetailsData,
  RateCardsConfigUpsertPayload
} from "../../../types"
import ManageRateCardsFormContent from "../../../common/ui/config-admin/config-rate-cards/manage/form-content"
import ScenarioPreferencesModal from "../../../common/ui/config-admin/config-rate-cards/scenario-preferences-modal"
import Tabs from "../../../common/ui/tabs"
import ConfigEditHeader from "../../../common/ui/config-admin/config-edit-header"
import {
  useDeleteRateCardsConfigMutation,
  useFetchDefaultRateCardsConfigQuery,
  useFetchRateCardsConfigQuery,
  useUpdateOrCreateRateCardsConfigMutation,
  useFetchProfilePreferenceMapQuery,
  useUpdateProfilePreferenceMapMutation
} from "../../../redux/queries"
import { ConfigEditPageSkeleton } from "../../../common/ui/skeletons"
import { useConfigEditPage } from "../use-config-edit-page"

const TABS = [
  { id: "general", label: "General" },
  { id: "terms", label: "Terms" }
]

const EMPTY_DETAILS: RateCardsConfigDetailsData = {
  max_results: 6,
  disable_cta: false,
  disable_avg: false,
  tabs: {
    purchase: [],
    refinance: []
  }
}

type RateCardsFormValues = Omit<RateCardsConfigDetails, "brand_id"> & {
  brand_id: string
}

const RateCardsSchema = object({
  name: string().required().min(1),
  details: object().required(),
  brand_id: string().required().min(1)
})

const RateCardsEditPage: FC = () => {
  const [searchParams] = useSearchParams()
  const brandIdFromQuery = searchParams.get("brand_id") ?? ""
  const [showPrefsModal, setShowPrefsModal] = useState(false)

  const [updateOrCreate, saveResult] =
    useUpdateOrCreateRateCardsConfigMutation()
  const [deleteRateCardsConfig, deleteResult] =
    useDeleteRateCardsConfigMutation()
  const [updatePrefMap, { isLoading: isSavingPrefs }] =
    useUpdateProfilePreferenceMapMutation()
  const [prefBrandId, setPrefBrandId] = useState(brandIdFromQuery)

  const {
    id,
    isCreate,
    formRef,
    setFormField,
    tab,
    setTab,
    brands,
    navigateBack
  } = useConfigEditPage<RateCardsFormValues>({
    tabs: TABS,
    tab: "rate-cards",
    label: "rate cards config",
    saveResult,
    deleteResult
  })

  const { data: prefMapData, isLoading: isLoadingPrefs } =
    useFetchProfilePreferenceMapQuery(
      { brand_id: prefBrandId },
      { skip: !prefBrandId || (tab !== "terms" && !showPrefsModal) }
    )

  const openPrefsModal = () => {
    const brandId = formRef.current?.values.brand_id ?? ""
    if (!brandId) return
    setPrefBrandId(brandId)
    setShowPrefsModal(true)
  }

  const handleSavePrefs = async (
    preferences: Record<string, number | null>
  ) => {
    if (!prefBrandId) return
    await updatePrefMap({
      profile_preferences: preferences,
      brand_id: Number(prefBrandId)
    })
    setShowPrefsModal(false)
  }

  const { data: rateCardsData, isLoading } = useFetchRateCardsConfigQuery(
    { id: id ?? "" },
    { skip: isCreate || !id }
  )

  const { data: defaultConfig } = useFetchDefaultRateCardsConfigQuery(
    {},
    { skip: !isCreate }
  )

  const defaultDetails =
    (defaultConfig as { details?: RateCardsConfigDetailsData })?.details ??
    EMPTY_DETAILS

  useEffect(() => {
    if (rateCardsData?.brand_id) {
      setPrefBrandId(String(rateCardsData.brand_id))
    }
  }, [rateCardsData?.brand_id])

  const initialValues: RateCardsFormValues = rateCardsData
    ? {
        ...rateCardsData,
        brand_id: String(rateCardsData.brand_id),
        name: rateCardsData.name ?? "",
        details: rateCardsData.details ?? EMPTY_DETAILS
      }
    : {
        name: "",
        id: "",
        brand_id: brandIdFromQuery,
        is_default: "0",
        details: defaultDetails
      }

  const onSubmit = () => {
    const vals = formRef.current?.values
    if (!vals) return
    const payload: RateCardsConfigUpsertPayload = {
      ...vals,
      brand_id: Number(vals.brand_id),
      is_default: vals.is_default,
      details: vals.details
    }
    if (vals.id) {
      payload.id = vals.id
    }
    updateOrCreate(payload)
  }

  const onDelete = () => {
    if (rateCardsData?.id) deleteRateCardsConfig(rateCardsData.id)
  }

  if (!isCreate && isLoading) {
    return <ConfigEditPageSkeleton tabCount={2} />
  }

  const brandName = brands?.find(
    b => b.id === String(rateCardsData?.brand_id)
  )?.name
  const pageTitle = isCreate
    ? "New rate cards config"
    : brandName
    ? `${brandName} — ${rateCardsData?.name ?? "Rate cards"}`
    : rateCardsData?.name ?? "Rate cards config"

  return (
    <div className="min-h-screen bg-gray-50">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        innerRef={formRef}
        validationSchema={RateCardsSchema}
        enableReinitialize
      >
        {({ values, isValid, dirty }) => (
          <>
            <ConfigEditHeader
              allowEditName
              backLabel="Rate cards"
              namePlaceholder="Rate cards config name"
              name={values.name}
              pageTitle={pageTitle}
              isCreate={isCreate}
              isValid={isValid}
              dirty={dirty}
              isDefault={values.is_default === "1"}
              brands={brands ?? []}
              brandId={values.brand_id}
              onNameChange={val => setFormField("name", val)}
              onDefaultChange={v => setFormField("is_default", v ? "1" : "0")}
              onBrandChange={val => {
                setFormField("brand_id", val)
                setPrefBrandId(val)
              }}
              onDelete={onDelete}
              onCancel={() => navigateBack(values.brand_id)}
              onSubmit={onSubmit}
              onBack={() => navigateBack(values.brand_id)}
            >
              <div className="relative">
                <button
                  type="button"
                  onClick={openPrefsModal}
                  disabled={!values.brand_id}
                  title={
                    values.brand_id
                      ? "Scenario Preferences"
                      : "Select a brand first"
                  }
                  className="flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Settings size={14} />
                  Settings
                </button>
              </div>
            </ConfigEditHeader>

            <ScenarioPreferencesModal
              show={showPrefsModal}
              onClose={() => setShowPrefsModal(false)}
              data={prefMapData}
              isLoading={isLoadingPrefs}
              isSaving={isSavingPrefs}
              onSave={handleSavePrefs}
            />

            <Tabs
              currentTab={tab}
              tabs={TABS}
              onChange={setTab}
              className="px-6 bg-white"
            />

            <div className="bg-white">
              <div className="max-w-2xl py-6 px-6">
                <ManageRateCardsFormContent
                  tab={tab}
                  details={values.details}
                  setDetails={next => setFormField("details", next)}
                  scenarioPreferences={prefMapData}
                  isLoadingScenarios={isLoadingPrefs}
                />
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  )
}

export default RateCardsEditPage
